import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { programData, getWeekItems } from '../data/course';
import {
  HiOutlinePlay,
  HiOutlineClipboardDocumentCheck,
  HiOutlineChatBubbleLeftRight,
} from 'react-icons/hi2';

function SidebarToggleIcon({ className = "w-4 h-4" }) {
  return (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" className={className}>
      <rect x="2" y="2" width="12" height="12" rx="2" />
      <line x1="6" y1="2" x2="6" y2="14" />
    </svg>
  );
}

function NavItem({ to, label, icon: Icon, isActive, indent, isLast, onNavigate }) {
  return (
    <Link
      to={to}
      onClick={onNavigate}
      className={`group flex items-center gap-2.5 rounded-lg text-[13px] leading-snug transition-all duration-150 ${
        indent ? 'pl-9 pr-3 py-[6px]' : 'px-3 py-[7px]'
      } ${
        isActive
          ? 'text-white bg-white/[0.08]'
          : 'text-gray-400 hover:text-gray-200 hover:bg-white/[0.05]'
      }`}
    >
      {Icon && (
        <span className={`flex-shrink-0 transition-colors duration-150 ${
          isActive ? 'text-white' : 'text-gray-500 group-hover:text-gray-400'
        }`}>
          <Icon className="w-[15px] h-[15px]" />
        </span>
      )}
      <span className="truncate">{label}</span>
    </Link>
  );
}

function WeekGroup({ week, currentPath, onNavigate }) {
  const items = getWeekItems(week);
  const liveItem = items.find((i) => i.type === 'live');
  const articles = items.filter((i) => i.type === 'article');
  const worksheets = items.filter((i) => i.type === 'worksheet');

  return (
    <div className="mb-7">
      {/* Week header */}
      <h3 className="px-3 text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-2">
        Week {week.number} &middot; {week.title}
      </h3>

      <div className="space-y-px">
        {/* Live session */}
        {liveItem && (
          <div className="relative">
            {/* Vertical connector line from live to articles */}
            <div className="absolute left-[22px] top-[30px] bottom-[-2px] w-px bg-white/[0.06]" />
            <NavItem
              to={`/content/${liveItem.id}`}
              label={liveItem.title}
              icon={HiOutlinePlay}
              isActive={currentPath === `/content/${liveItem.id}`}
              onNavigate={onNavigate}
            />
          </div>
        )}

        {/* Articles cluster — indented under the vertical line */}
        {articles.length > 0 && (
          <div className="relative">
            {/* Vertical line continuing through articles */}
            <div className="absolute left-[22px] top-0 bottom-0 w-px bg-white/[0.06]" />
            <div className="space-y-px">
              {articles.map((article, i) => (
                <NavItem
                  key={article.id}
                  to={`/content/${article.id}`}
                  label={article.title}
                  isActive={currentPath === `/content/${article.id}`}
                  indent
                  isLast={i === articles.length - 1}
                  onNavigate={onNavigate}
                />
              ))}
            </div>
          </div>
        )}

        {/* Worksheets — back to root level with their own icon */}
        {worksheets.map((ws) => (
          <NavItem
            key={ws.id}
            to={`/content/${ws.id}`}
            label={ws.title}
            icon={HiOutlineClipboardDocumentCheck}
            isActive={currentPath === `/content/${ws.id}`}
            onNavigate={onNavigate}
          />
        ))}
      </div>
    </div>
  );
}

export default function Sidebar({ isOpen, onToggle }) {
  const location = useLocation();
  const currentPath = location.pathname;

  // On mobile, close sidebar after navigation
  function handleMobileNav() {
    if (window.innerWidth < 1024) {
      onToggle();
    }
  }

  return (
    <>
      {/* Mobile overlay */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="fixed inset-0 bg-black/60 z-30 lg:hidden"
            onClick={onToggle}
          />
        )}
      </AnimatePresence>

      <motion.aside
        initial={false}
        animate={{ width: isOpen ? 288 : 0 }}
        transition={{ duration: 0.2, ease: [0.25, 0.1, 0.25, 1] }}
        className={`flex-shrink-0 bg-[#0a0a0b] h-screen sticky top-0 overflow-hidden z-40 ${
          isOpen ? 'border-r border-white/[0.06]' : ''
        } max-lg:fixed max-lg:left-0 max-lg:top-0`}
      >
        <div className="w-72 h-full flex flex-col overflow-hidden">
          {/* Header area */}
          <div className="px-5 pt-5 pb-0 flex-shrink-0">
            {/* Toggle */}
            <button
              onClick={onToggle}
              className="p-1.5 rounded-md text-gray-500 hover:text-gray-300 hover:bg-white/[0.06] transition-colors mb-5"
            >
              <SidebarToggleIcon />
            </button>

            {/* Program title */}
            <Link to="/" onClick={handleMobileNav} className="block mb-4">
              <h2 className="text-[15px] font-semibold text-white leading-snug tracking-tight">
                Group Coaching
              </h2>
              <p className="text-[13px] text-gray-400 mt-1 leading-snug">
                with Dr. Shilpa Saxena
              </p>
            </Link>

            {/* About Dr. Saxena — right below the title */}
            <Link
              to="/about"
              onClick={handleMobileNav}
              className={`flex items-center gap-3 px-2 py-2.5 -mx-2 rounded-xl transition-colors duration-150 mb-4 ${
                currentPath === '/about'
                  ? 'bg-white/[0.08]'
                  : 'hover:bg-white/[0.05]'
              }`}
            >
              <div className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0 ring-1 ring-white/[0.1]">
                <img
                  src="https://forumhealth.com/wp-content/uploads/2019/05/Shilpa-Saxena-1-scaled-e1699545138555-768x768.webp"
                  alt="Dr. Shilpa Saxena"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="min-w-0">
                <p className={`text-[12px] font-medium leading-tight truncate ${
                  currentPath === '/about' ? 'text-white' : 'text-gray-300'
                }`}>
                  Dr. Shilpa P. Saxena, MD
                </p>
                <p className="text-[11px] text-gray-500 leading-tight mt-0.5">
                  Board-Certified Family Physician
                </p>
              </div>
            </Link>
          </div>

          {/* Divider */}
          <div className="mx-5 border-t border-white/[0.06]" />

          {/* Scrollable nav */}
          <nav className="flex-1 overflow-y-auto px-3 pt-5 pb-8">
            {programData.weeks.map((week) => (
              <WeekGroup
                key={week.number}
                week={week}
                currentPath={currentPath}
                onNavigate={handleMobileNav}
              />
            ))}

            {/* Bottom links */}
            <div className="border-t border-white/[0.06] mt-3 pt-5 space-y-px">
              <NavItem
                to="/community"
                label="Community"
                icon={HiOutlineChatBubbleLeftRight}
                isActive={currentPath === '/community'}
                onNavigate={handleMobileNav}
              />
            </div>
          </nav>
        </div>
      </motion.aside>
    </>
  );
}
